import React from 'react';
import clsx from 'clsx';
import {InputAdornment} from '@mui/material';
import {Alert} from '@netx/core-components/Alert';
import {Button} from '@netx/core-components/Button';
import {TextField, type TextFieldProps} from '@netx/core-components/TextField';
import {
	composeClasses,
	styled,
	type WithClasses,
} from '@netx/core-components/Theme';
import {
	URL_TEXT_FIELD_ACTION_LABEL,
	URL_TEXT_FIELD_LABEL,
	URL_TEXT_FIELD_MESSAGES,
} from './locales';
import {validateURL} from './utils';
import {
	getURLTextFieldUtilityClass,
	URL_TEXT_FIELD_CLASS_NAME,
	type URLTextFieldClasses,
} from './urlTextFieldClasses';

export type URLTextFieldOwnerState = WithClasses<
	URLTextFieldClasses,
	{
		valid?: boolean;
		invalid?: boolean;
	}
>;

const useUtilityClasses = (ownerState: URLTextFieldOwnerState) => {
	const {classes, invalid, valid} = ownerState;
	const slots = {
		root: ['root'],
		endAdornment: ['endAdornment'],
		status: ['status', invalid && 'invalid', valid && 'valid'],
	};
	return composeClasses(slots, getURLTextFieldUtilityClass, classes);
};

export const URLTextFieldRoot = styled('div', {
	name: URL_TEXT_FIELD_CLASS_NAME,
	slot: 'Root',
})(({theme}) => ({
	width: '100%',
}));

export const URLTextFieldInputAddon = styled('div', {
	name: URL_TEXT_FIELD_CLASS_NAME,
	slot: 'EndAdornment',
})(({theme}) => ({
	marginLeft: theme.spacing(1),
	marginRight: theme.spacing(-1),
	paddingLeft: theme.spacing(0.5),
	borderLeft: `1px solid ${theme.palette.custom.border}`,
}));

const URLTextFieldStatus = styled(Alert, {
	name: URL_TEXT_FIELD_CLASS_NAME,
	slot: 'Status',
})(({theme}) => ({
	margin: theme.spacing(1, 0),
}));

const getEndpointStatusSeverity = (checking: boolean, valid?: boolean) => {
	if (checking) return 'info';
	if (valid === false) return 'error';
	if (valid === true) return 'success';
};

export interface URLTextFieldProps
	extends Partial<Omit<TextFieldProps, 'classes'>>,
		WithClasses<URLTextFieldClasses, {}> {
	changed?: boolean;
	testButtonLabel?: string;
	testingURL?: boolean;
	testingUrlMessage?: string;
	urlAccessible?: boolean;
	urlAccessibleMessage?: string;
	urlInaccessibleMessage?: string;
	value?: string | null;
	onTestURL?: () => void;
}

export const URLTextFieldPrimitive = React.forwardRef<
	HTMLDivElement,
	URLTextFieldProps
>(
	(
		{
			classes: _classes,
			className,
			changed,
			readOnly,
			testButtonLabel,
			testingURL,
			testingUrlMessage,
			urlAccessible,
			urlAccessibleMessage,
			urlInaccessibleMessage,
			value,
			onTestURL,
			...props
		},
		ref,
	) => {
		value = value
			? !readOnly
				? value.replace(/^http(s)?:\/\//, '')
				: value
			: '';

		const isValid = validateURL(value);

		const ownerState: URLTextFieldOwnerState = {
			classes: _classes,
			className,
			invalid: urlAccessible === false,
			valid: urlAccessible === true,
		};

		const classes = useUtilityClasses(ownerState);

		const getEndpointStatusLabel = () => {
			if (testingURL)
				return testingUrlMessage || URL_TEXT_FIELD_MESSAGES.TESTING;
			if (urlAccessible === false)
				return urlInaccessibleMessage || URL_TEXT_FIELD_MESSAGES.INACCESSIBLE;
			if (urlAccessible === true)
				return urlAccessibleMessage || URL_TEXT_FIELD_MESSAGES.ACCESSIBLE;
		};

		return (
			<URLTextFieldRoot className={clsx(classes.root, className)}>
				<TextField
					label={URL_TEXT_FIELD_LABEL.NAME}
					name="url"
					{...props}
					ref={ref}
					readOnly={readOnly}
					value={value}
					InputProps={{
						endAdornment: onTestURL ? (
							<URLTextFieldInputAddon className={classes.endAdornment}>
								<Button
									data-testid="url-text-field-test"
									disabled={testingURL || !isValid}
									size="small"
									variant="text"
									onClick={() => onTestURL()}
								>
									{testButtonLabel || URL_TEXT_FIELD_ACTION_LABEL.TEST}
								</Button>
							</URLTextFieldInputAddon>
						) : undefined,

						startAdornment: !readOnly ? (
							<InputAdornment position="start" disablePointerEvents>
								https://
							</InputAdornment>
						) : undefined,
					}}
				/>

				{(!changed && testingURL) || urlAccessible != null ? (
					<URLTextFieldStatus
						className={classes.status}
						severity={getEndpointStatusSeverity(
							testingURL === true,
							urlAccessible,
						)}
						variant="outlined"
					>
						{getEndpointStatusLabel()}
					</URLTextFieldStatus>
				) : null}
			</URLTextFieldRoot>
		);
	},
);

export const URLTextField = URLTextFieldPrimitive;

export default URLTextField;
